import { useEffect, useMemo, useState } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'

import { AddButton, DeleteButton } from '@/components/Elements/Button'
import useVendor from '@/features/vendors/useVendor'
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import { PRStatus } from '@eapproval/offer-shared'
import { MdOutlineInfo, MdOutlineSubdirectoryArrowRight } from 'react-icons/md'
import { PRAccessProvider, usePRAccess } from './PRAccessProvider'
import PRGeneralInfoSection from './PRGeneralInfoSection'
import VendorSelectionSubTab from './VendorSelectionSubTab/VendorSelectionSubTab'
import moment from 'moment'

//Constant

const getPRStatusBadge = (submissions = []) => {
  if (!Array.isArray(submissions) || submissions.length === 0) {
    return { label: 'No PR', colorScheme: 'gray' }
  }

  const latestStatus = submissions[submissions.length - 1]?.status

  switch (latestStatus) {
    case PRStatus.DRAFT:
      return { label: PRStatus.DRAFT, colorScheme: 'gray' }
    case PRStatus.SUBMITTED:
      return { label: PRStatus.SUBMITTED, colorScheme: 'blue' }
    case PRStatus.APPROVED:
      return { label: PRStatus.APPROVED, colorScheme: 'green' }
    case PRStatus.REJECTED:
      return { label: PRStatus.REJECTED, colorScheme: 'red' }
    case PRStatus.PARKED:
      return { label: PRStatus.PARKED, colorScheme: 'yellow' }
    default:
      return { label: latestStatus ?? 'Unknown', colorScheme: 'gray' }
  }
}

//Helper

const PRTabContent = ({ selectedSubSystemIndex, setSelectedSubSystemIndex }) => {
  const { getAddVendorSelectionBlockReason, canDeleteVendorSelection } = usePRAccess()
  const params = new URLSearchParams(window.location.search)
  const PRCostBudgetIndex = params.get('PRCostBudgetIndex')
  const PRVendorSelectionIndex = params.get('PRVendorSelectionIndex')
  const [subTabIndex, setSubTabIndex] = useState(0)
  const { getValues, setValue } = useFormContext()
  const costBudgetWatchFields = useWatch({ name: 'sourcing.costBudgets' })
  const subSystems = useWatch({ name: 'subSystems' })
  const bookingCreateDate = useWatch({ name: `booking.bookingCreateDate` })

  const {
    fields: costBudgetFields,
    append: appendCostBudget,
    remove: removeCostBudget,
    insert: insertCostBudget,
  } = useFieldArray({ name: 'sourcing.costBudgets' })
  const { useVendorOptions } = useVendor()
  const { data: vendorOptions } = useVendorOptions({ valueKey: '_id' })

  const costBudgets = costBudgetFields.map((field, index) => {
    return {
      ...field,
      ...costBudgetWatchFields[index],
    }
  })

  const tabItems = useMemo(() => {
    const items = []
    const selectedSubSystemName =
      selectedSubSystemIndex !== null ? subSystems?.[selectedSubSystemIndex]?.projectName : null

    costBudgets.forEach((costBudget, index) => {
      const matchesSubSystem = selectedSubSystemName === null || costBudget?.detail?.subSystem === selectedSubSystemName

      if (!matchesSubSystem) {
        return
      }

      //
      const subSystemIndex = subSystems?.findIndex(
        (subSystem) => subSystem?.projectName === costBudget?.detail?.subSystem
      )

      const costBudgetName =
        costBudget?.detail?.purpose === '' ? `Cost Budget ${index + 1}` : costBudget?.detail?.purpose
      const RFQCode = costBudget?.detail?.RFQCode || '-'

      items.push({
        name: costBudgetName,
        RFQCode: RFQCode,
        type: 'costBudget',
        costBudgetIndex: index,
        costBudget,
        isAfterBooking: bookingCreateDate && moment(bookingCreateDate).isBefore(moment(costBudget?.detail?.createdAt)),
      })

      costBudget.PR.vendorSelections.forEach((selection, vendorSelectionIndex) => {
        const selectedVendorId = selection?.vendor
        const selectedVendorDetail = (costBudget?.detail?.subcontractors ?? []).find(
          (detail) => String(detail?.vendor ?? '') === String(selectedVendorId ?? '')
        )
        const selectedVendorOption = (vendorOptions ?? []).find(
          (option) => String(option?.value ?? '') === String(selectedVendorId ?? '')
        )
        const selectedVendorName =
          selectedVendorOption?.label ?? selectedVendorDetail?.vendor?.name ?? selectedVendorDetail?.vendorName

        items.push({
          name: selectedVendorName
            ? `V${vendorSelectionIndex + 1} - ${selectedVendorName}`
            : `Vendor Selection - ${vendorSelectionIndex + 1}`,
          type: 'vendorSelection',
          costBudgetIndex: index,
          vendorSelectionIndex: vendorSelectionIndex,
          costBudget: costBudget,
          subSystemIndex: subSystemIndex,
        })
      })
    })

    return items
  }, [costBudgets, selectedSubSystemIndex, subSystems, vendorOptions])

  const getSubSystemIndexByCostBudgetIndex = (costBudgetIndex) => {
    if (!Number.isInteger(costBudgetIndex)) {
      return null
    }

    const costBudgetSubSystem = costBudgets?.[costBudgetIndex]?.detail?.subSystem
    if (!costBudgetSubSystem || !Array.isArray(subSystems)) {
      return null
    }

    const matchedSubSystemIndex = subSystems.findIndex((subSystem) => subSystem?.projectName === costBudgetSubSystem)
    return matchedSubSystemIndex >= 0 ? matchedSubSystemIndex : null
  }

  useEffect(() => {
    if (PRCostBudgetIndex === null || PRVendorSelectionIndex === null) {
      return
    }

    const targetCostBudgetIndex = Number(PRCostBudgetIndex)
    const targetVendorSelectionIndex = Number(PRVendorSelectionIndex)

    if (!Number.isInteger(targetCostBudgetIndex) || !Number.isInteger(targetVendorSelectionIndex)) {
      return
    }

    const matchedSubSystemIndex = getSubSystemIndexByCostBudgetIndex(targetCostBudgetIndex)
    setSelectedSubSystemIndex(matchedSubSystemIndex)

    const targetVendorSelectionTabIndex = tabItems
      .filter((item) => item.type === 'vendorSelection' && item.subSystemIndex === selectedSubSystemIndex)

      .findIndex(
        (item) =>
          item.costBudgetIndex === targetCostBudgetIndex && item.vendorSelectionIndex === targetVendorSelectionIndex
      )

    if (targetVendorSelectionTabIndex > -1) {
      setSubTabIndex(targetVendorSelectionTabIndex)

      const updatedParams = new URLSearchParams(window.location.search)
      updatedParams.delete('PRCostBudgetIndex')
      updatedParams.delete('PRVendorSelectionIndex')
      updatedParams.delete('PRSubmissionIndex')

      const updatedQuery = updatedParams.toString()
      const updatedUrl = `${window.location.pathname}${updatedQuery ? `?${updatedQuery}` : ''}${window.location.hash}`

      window.history.replaceState({}, '', updatedUrl)
    }
  }, [PRCostBudgetIndex, PRVendorSelectionIndex, tabItems])

  const handleAddVendorSelection = (costBudgetIndex) => {
    const selectionPath = `sourcing.costBudgets.${costBudgetIndex}.PR.vendorSelections`
    const existingSelections = getValues(selectionPath) ?? []
    const createdVendorSelectionIndex = existingSelections.length

    setValue(
      selectionPath,
      [...existingSelections, { vendor: '', uploadHistoryIndex: -1, quotationDetails: [], remark: '' }],
      { shouldDirty: true }
    )

    const selectedSubSystemName =
      selectedSubSystemIndex !== null ? subSystems?.[selectedSubSystemIndex]?.projectName : null
    const visibleCostBudgetIndexes = costBudgets
      .map((costBudget, index) => ({ costBudget, index }))
      .filter(
        ({ costBudget }) => selectedSubSystemName === null || costBudget?.detail?.subSystem === selectedSubSystemName
      )
      .map(({ index }) => index)

    const tabOffset = visibleCostBudgetIndexes.reduce((total, index) => {
      if (index >= costBudgetIndex) {
        return total
      }

      const previousSelections =
        getValues(`sourcing.costBudgets.${index}.PR.vendorSelections`) ?? costBudgets[index]?.PR?.vendorSelections ?? []

      return total + previousSelections.length
    }, 0)

    setSubTabIndex(tabOffset + createdVendorSelectionIndex)

    const matchedSubSystemIndex = getSubSystemIndexByCostBudgetIndex(costBudgetIndex)
    setSelectedSubSystemIndex(matchedSubSystemIndex)
  }

  const handleDeleteVendorSelection = (costBudgetIndex, vendorSelectionIndex) => {
    const selectionPath = `sourcing.costBudgets.${costBudgetIndex}.PR.vendorSelections`
    const existingSelections = getValues(selectionPath) ?? []

    if (!Array.isArray(existingSelections)) return
    if (vendorSelectionIndex < 0 || vendorSelectionIndex >= existingSelections.length) return

    const updatedSelections = existingSelections.filter((_, index) => index !== vendorSelectionIndex)

    setValue(selectionPath, updatedSelections, { shouldDirty: true })
    setSubTabIndex(-1)
  }

  return (
    <Flex gap="5" direction="column">
      <PRGeneralInfoSection />

      {Array.isArray(subSystems) && subSystems.length > 0 && (
        <ButtonGroup size="sm" flexWrap="wrap" gap="2">
          <Button
            colorScheme={selectedSubSystemIndex === null ? 'blue' : 'gray'}
            variant={selectedSubSystemIndex === null ? 'solid' : 'outline'}
            onClick={() => {
              setSubTabIndex(-1)
              setSelectedSubSystemIndex(null)
            }}
          >
            All
          </Button>
          {subSystems.map((subSystem, index) => (
            <Button
              key={index}
              colorScheme={selectedSubSystemIndex === index ? 'blue' : 'gray'}
              variant={selectedSubSystemIndex === index ? 'solid' : 'outline'}
              onClick={() => {
                setSubTabIndex(-1)
                setSelectedSubSystemIndex(selectedSubSystemIndex === index ? null : index)
              }}
            >
              {`1.${index + 1}`} {subSystem.projectName}
            </Button>
          ))}
        </ButtonGroup>
      )}
      <Tabs
        size="md"
        variant="enclosed"
        index={subTabIndex}
        orientation="vertical"
        isLazy
        onChange={(index) => {
          setSubTabIndex(index)

          const selectedTab = tabItems[index]
          const matchedSubSystemIndex = getSubSystemIndexByCostBudgetIndex(selectedTab?.costBudgetIndex)
          setSelectedSubSystemIndex(matchedSubSystemIndex)
        }}
      >
        <Flex>
          <TabList
            overflowX={'scroll'}
            overflowY={'hidden'}
            sx={{
              '::-webkit-scrollbar': {
                display: 'none',
              },
            }}
            minW="300px"
          >
            {tabItems.map((tab, tabIndex) => {
              if (tab.type === 'costBudget') {
                const addVendorSelectionBlockReason = getAddVendorSelectionBlockReason(tab.costBudgetIndex)
                const canShowAddButton = addVendorSelectionBlockReason === null
                const toolTipLabel = `${tab.name}, Created ${tab.isAfterBooking ? 'after' : 'before'} booking, at ${moment(tab.costBudget.createdAt).format('YYYY-MM-DD HH:mm:ss')}`
                const accentBorderColor = !canShowAddButton ? 'gray.300' : tab.isAfterBooking ? 'green.400' : 'blue.400'

                return (
                  <Flex
                    key={'PRTab_' + tabIndex}
                    gap={2}
                    direction="column"
                    minW="150px"
                    maxW="300px"
                    alignItems={'start'}
                    borderTopEndRadius={0}
                    px="4"
                    py="2"
                    borderTop={'1px solid rgb(226, 232, 240)'}
                    borderLeft={'1px solid rgb(226, 232, 240)'}
                    borderRight={'1px solid white'}
                    bgColor={'gray.50'}
                    borderColor={'rgb(237, 242, 247)'}
                  >
                    <Flex
                      direction={'column'}
                      gap={1}
                      w={'100%'}
                      pl="2"
                      borderLeft={'3px solid'}
                      borderColor={accentBorderColor}
                    >
                      <Flex justifyContent={'space-between'} alignItems={'center'} w={'100%'} gap={1}>
                        <Tooltip label={toolTipLabel}>
                          <Text
                            textDecoration={'none'}
                            textDecorationThickness={'3px'}
                            textDecorationColor={'teal.400'}
                            textUnderlineOffset={'5px'}
                            w={'100%'}
                            textOverflow={'hidden'}
                            whiteSpace={'nowrap'}
                            overflow={'hidden'}
                            fontWeight={'bold'}
                          >
                            {tab.name}
                          </Text>
                        </Tooltip>
                        {canShowAddButton ? (
                          <AddButton onClick={() => handleAddVendorSelection(tab.costBudgetIndex)} />
                        ) : (
                          <Tooltip label={`${addVendorSelectionBlockReason}`}>
                            <Box
                              as="span"
                              display="inline-flex"
                              justifyContent={'center'}
                              w="24px"
                              color="gray.300"
                              flexShrink={0}
                              tabIndex={0}
                              aria-label={`${addVendorSelectionBlockReason}`}
                            >
                              <MdOutlineInfo size={24} />
                            </Box>
                          </Tooltip>
                        )}
                      </Flex>
                      <Flex>
                        <Text fontSize="0.75rem" color="gray.500">
                          {tab.RFQCode}
                        </Text>
                      </Flex>
                    </Flex>
                  </Flex>
                )
              } else {
                const canShowDeleteButton = canDeleteVendorSelection(tab.costBudgetIndex, tab.vendorSelectionIndex)
                const currentVendorSelection = tab?.costBudget?.PR?.vendorSelections?.[tab.vendorSelectionIndex]
                const statusBadge = getPRStatusBadge(currentVendorSelection?.submissions)

                return (
                  <Tab
                    key={'PRTab_' + tabIndex}
                    as={Flex}
                    gap={2}
                    minW="150px"
                    maxW="300px"
                    alignItems={'start'}
                    borderTopEndRadius={0}
                    borderRight={'0'}
                    borderTop={'0'}
                    _selected={{
                      border: '1px solid rgb(226, 232, 240)',
                      borderRight: '1px solid white',
                      color: 'teal.500',
                    }}
                  >
                    <Box mr="2">
                      <MdOutlineSubdirectoryArrowRight size={'20px'} />
                    </Box>
                    <Tooltip label={tab.name}>
                      <Flex justifyContent={'space-between'} alignItems={'center'} w={'100%'}>
                        <Text
                          fontWeight={'normal'}
                          textDecoration={'none'}
                          textDecorationThickness={'3px'}
                          textDecorationColor={'teal.400'}
                          textUnderlineOffset={'5px'}
                          w={'100%'}
                          textOverflow={'hidden'}
                          whiteSpace={'nowrap'}
                          overflow={'hidden'}
                        >
                          {tab.name}
                        </Text>
                        <Badge colorScheme={statusBadge.colorScheme} fontSize="0.65rem" mr="2">
                          {statusBadge.label}
                        </Badge>
                        {canShowDeleteButton && (
                          <DeleteButton
                            onClick={() => handleDeleteVendorSelection(tab.costBudgetIndex, tab.vendorSelectionIndex)}
                          />
                        )}
                      </Flex>
                    </Tooltip>
                  </Tab>
                )
              }
            })}
          </TabList>
        </Flex>

        <TabPanels>
          {tabItems
            .filter((item) => item.type === 'vendorSelection')
            .map((item, itemIndex) => (
              <TabPanel
                key={`PRTabPanel_${itemIndex}`}
                border="1px solid rgb(226, 232, 240)"
                w={'100%'}
                borderColor={''}
                overflowY={'scroll'}
              >
                <VendorSelectionSubTab
                  costBudgetIndex={item.costBudgetIndex}
                  vendorSelectionIndex={item.vendorSelectionIndex}
                />
              </TabPanel>
            ))}
        </TabPanels>
      </Tabs>
    </Flex>
  )
}

const PRTab = () => {
  const [selectedSubSystemIndex, setSelectedSubSystemIndex] = useState(null)

  return (
    <PRAccessProvider selectedSubSystemIndex={selectedSubSystemIndex}>
      <PRTabContent
        selectedSubSystemIndex={selectedSubSystemIndex}
        setSelectedSubSystemIndex={setSelectedSubSystemIndex}
      />
    </PRAccessProvider>
  )
}

export default PRTab
